import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faArrowTrendUp,
    faBuildingColumns,
    faChartLine,
    faCoins,
    faGem,
    faHandHoldingDollar,
    faScaleBalanced,
    faShieldHalved,
    faUserTie,
} from '@fortawesome/free-solid-svg-icons';
import { SCREENER_QUERY_CLAUSE_SEPARATOR } from './screenerIdeaQuery.util';

const Q = SCREENER_QUERY_CLAUSE_SEPARATOR;

export interface DiscoveryScreenCard {
    id: string;
    title: string;
    /** Short helper line shown under the title on strategy cards. */
    subtext?: string;
    /** Combined screener clause(s) — joined with ~~~ for multi-filter queries. */
    query: string;
    icon: IconDefinition;
    iconBgClassName: string;
    iconClassName: string;
}

export interface DiscoverySection {
    id: string;
    title: string;
    titlePrefix?: string;
    cards: DiscoveryScreenCard[];
}

export const SCREENER_FILTER_SHORTCUTS = [
    'Revenue',
    'Earnings',
    'ROE',
    'Debt',
    'Dividends',
    'Market Cap',
    'Cash Flow',
] as const;

/** Number of filter shortcuts shown inline on mobile before the overflow menu. */
export const SCREENER_FILTER_SHORTCUTS_MOBILE_VISIBLE = 4;

/** Number of popular search cards shown on mobile (desktop shows all). */
export const POPULAR_SEARCHES_MOBILE_VISIBLE = 5;

export const DISCOVERY_SECTIONS: DiscoverySection[] = [
    {
        id: 'popular-searches',
        title: 'Popular Searches',
        cards: [
            {
                id: 'high-growth-profitable',
                title: 'High Growth & Profitable',
                subtext: 'Fast growth with positive earnings',
                query: `revenue_growth_past_3_years_cagr > 15%${Q}net_income_ttm > 0`,
                icon: faChartLine,
                iconBgClassName: 'bg-emerald-50',
                iconClassName: 'text-emerald-600',
            },
            {
                id: 'earnings-accelerators',
                title: 'Earnings Accelerators',
                subtext: 'Strong EPS and revenue momentum',
                query: `eps_growth_past_1_year_cagr > 15%${Q}revenue_growth_past_1_year_cagr > 10%`,
                icon: faArrowTrendUp,
                iconBgClassName: 'bg-emerald-50',
                iconClassName: 'text-emerald-600',
            },
            {
                id: 'heavy-insider-buying',
                title: 'Heavy Insider Buying',
                subtext: 'Insiders buying with rising profits',
                query: `insider_trading_1_year_trend = BUY${Q}revenue_growth_past_1_year_cagr > 10%${Q}net_income_growth_past_1_year_cagr > 10%`,
                icon: faHandHoldingDollar,
                iconBgClassName: 'bg-emerald-50',
                iconClassName: 'text-emerald-600',
            },
            {
                id: 'founder-lead',
                title: 'Founder Lead',
                subtext: 'Founder-led with long-term growth',
                query: `founder_ceo_operator = true${Q}revenue_growth_past_5_years_cagr > 10%`,
                icon: faUserTie,
                iconBgClassName: 'bg-emerald-50',
                iconClassName: 'text-emerald-600',
            },
            {
                id: 'steady-compounders',
                title: 'Steady Compounders',
                subtext: 'Consistent revenue, income & ROE',
                query: `revenue_growth_past_5_years_cagr > 10%${Q}net_income_growth_past_1_year_cagr > 15%${Q}roe_growth_past_5_years_average > 15%`,
                icon: faChartLine,
                iconBgClassName: 'bg-emerald-50',
                iconClassName: 'text-emerald-600',
            },
            {
                id: 'quality-operators',
                title: 'Quality Operators',
                subtext: 'High margins and asset efficiency',
                query: `gross_profit_margin_ttm >= 30%${Q}return_on_assets_ttm >= 5%`,
                icon: faGem,
                iconBgClassName: 'bg-emerald-50',
                iconClassName: 'text-emerald-600',
            },
            {
                id: 'dividend-champions',
                title: 'Dividend Champions',
                subtext: 'Yield plus positive free cash flow',
                query: `dividend_yield >= 2.5%${Q}free_cash_flow_ttm > 0`,
                icon: faCoins,
                iconBgClassName: 'bg-emerald-50',
                iconClassName: 'text-emerald-600',
            },
            {
                id: 'low-debt-leaders',
                title: 'Low Debt Leaders',
                subtext: 'Minimal debt to equity',
                query: 'debt_to_equity_ratio_current <= 0.5',
                icon: faScaleBalanced,
                iconBgClassName: 'bg-emerald-50',
                iconClassName: 'text-emerald-600',
            },
            {
                id: 'strong-balance-sheet',
                title: 'Strong Balance Sheet',
                subtext: 'Low debt and healthy margins',
                query: `debt_to_equity_ratio_current <= 1${Q}net_profit_margin_ttm >= 10%`,
                icon: faShieldHalved,
                iconBgClassName: 'bg-emerald-50',
                iconClassName: 'text-emerald-600',
            },
            {
                id: 'cash-flow-generators',
                title: 'Cash Flow Generators',
                subtext: 'Improving revenue and margins',
                query: `has_revenue_improved_past_3_years = true${Q}operating_profit_margin_ttm >= 10%`,
                icon: faBuildingColumns,
                iconBgClassName: 'bg-emerald-50',
                iconClassName: 'text-emerald-600',
            },
        ],
    },
];

/** Flat list of all discovery cards — used for active-state lookup from URL query. */
export const ALL_DISCOVERY_CARDS: DiscoveryScreenCard[] = DISCOVERY_SECTIONS.flatMap(
    (section) => section.cards,
);
